// 2026-07-07: PMI Special-Process — Route Card Follower (the operator parameter-capture screen)
// Renders a special-process recipe's substeps dynamically, pulls setpoints from tank_master
// (PCM-01), captures the mandatory operator columns (Time/Temp/pH/Voltage/Current), enforces
// water-break gates, flags out-of-limit readings → Quality (blue-card), operator e-sign + QA sign-off.
// Data-driven: adding a new NADCAP process = config (sp_recipes + tank_master), no code change.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import { listWorkOrders } from "../../Services/app/workorderservice";
import { clockIn, clockOut } from "../../Services/app/clockservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ScienceIcon from '@mui/icons-material/Science';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedIcon from '@mui/icons-material/Verified';
import PrintIcon from '@mui/icons-material/Print';
import BoltIcon from '@mui/icons-material/Bolt';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

// The 5 mandatory capture columns (grey columns on the paper follower).
const COLS = [
  { key: 'TIME',        field: 'time',    label: 'Time',    unit: '',   spKey: 'time',        names: ['Time'] },
  { key: 'TEMPERATURE', field: 'temp',    label: 'Temp',    unit: '°C', spKey: 'temperature', names: ['Temperature'] },
  { key: 'PH',          field: 'ph',      label: 'pH',      unit: '',   spKey: 'ph',          names: ['pH'] },
  { key: 'VOLTAGE',     field: 'voltage', label: 'Voltage', unit: 'V',  spKey: 'voltage',     names: ['Voltage', 'Current & Voltage'] },
  { key: 'CURRENT',     field: 'current', label: 'Current', unit: '',   spKey: 'current',     names: ['Current & Voltage', 'Current'] },
];

const STATUS_INFO = {
  OPEN:         { color: BR.muted,   bg: '#f3f4f6', label: '○ OPEN' },
  IN_PROGRESS:  { color: BR.orange,  bg: '#fff7ed', label: '▶ IN PROGRESS' },
  DONE:         { color: BR.green,   bg: '#dcfce7', label: '✓ DONE' },
  HOLD:         { color: BR.amber,   bg: '#fef3c7', label: '⚠ QUALITY HOLD' },
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' +
         dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// ---- setpoint resolution from tank_master + substep override ---------------
function paramRange(p) {
  // returns {min, max, display} from a tank parameter object (control > spec > note parse)
  if (!p) return null;
  const num = (x) => (typeof x === 'number' ? x : (x !== undefined && x !== '' && !isNaN(+x) ? +x : undefined));
  let min, max;
  if (p.control && (p.control.min !== undefined || p.control.max !== undefined)) { min = num(p.control.min); max = num(p.control.max); }
  else if (p.spec && (p.spec.min !== undefined || p.spec.max !== undefined)) { min = num(p.spec.min); max = num(p.spec.max); }
  if (min === undefined && max === undefined && p.note) {
    const rng = String(p.note).match(/(-?\d+(?:\.\d+)?)\s*(?:-|–|to)\s*(-?\d+(?:\.\d+)?)/);
    if (rng) { min = +rng[1]; max = +rng[2]; }
    else {
      const mx = String(p.note).match(/(-?\d+(?:\.\d+)?)\s*max/i); if (mx) max = +mx[1];
      const mn = String(p.note).match(/(-?\d+(?:\.\d+)?)\s*min/i); if (mn) min = +mn[1];
    }
  }
  const parts = [];
  if (min !== undefined && max !== undefined) parts.push(`${min}–${max}`);
  else if (max !== undefined) parts.push(`≤${max}`);
  else if (min !== undefined) parts.push(`≥${min}`);
  else if (p.note) parts.push(String(p.note));
  if (p.optimum !== undefined) parts.push(`opt ${p.optimum}`);
  const display = (parts.join(' · ') + (p.uom && parts.length && !/ambient|conduct/i.test(parts[0]) ? '' : '')) || (p.note || '');
  return { min, max, display: parts.join(' · ') || (p.note || ''), uom: p.uom };
}

function setpointFor(tank, col, override, inline) {
  // substep-level override wins (e.g. BSA T-122 voltage 15±2, temp 19-21)
  if (override) {
    if (col.field === 'voltage' && override.voltage) {
      const o = override.voltage; const min = o.nominal - (o.tolerance || 0), max = o.nominal + (o.tolerance || 0);
      return { min, max, display: `${o.nominal}±${o.tolerance || 0} ${o.unit || 'V'}` };
    }
    if (col.field === 'temp' && override.temperature) {
      const o = override.temperature; return { min: o.min, max: o.max, display: `${o.min}–${o.max} ${o.unit || '°C'}` };
    }
    if (col.field === 'time' && override.time) {
      const o = override.time; return { min: o.min, max: o.max, display: `${o.min}–${o.max} ${o.unit || ''}` };
    }
  }
  // inline substep setpoints (NDT/passivation recipes that don't reference the PCM-01 master)
  if (inline && col.spKey && inline[col.spKey]) {
    const o = inline[col.spKey];
    if (o.mode === 'ambient') return { min: undefined, max: undefined, display: 'ambient', uom: o.unit };
    if (o.min !== undefined || o.max !== undefined) {
      const d = o.min !== undefined && o.max !== undefined ? `${o.min}–${o.max}` : (o.max !== undefined ? `≤${o.max}` : `≥${o.min}`);
      return { min: o.min, max: o.max, display: `${d}${o.unit ? ' ' + o.unit : ''}`, uom: o.unit };
    }
  }
  if (!tank || !Array.isArray(tank.parameters)) return null;
  // find the tank parameter whose name matches this column
  const p = tank.parameters.find(pp => col.names.some(n => (pp.param || '').toLowerCase().startsWith(n.toLowerCase())))
         || tank.parameters.find(pp => col.names.some(n => (pp.param || '').toLowerCase().includes(n.toLowerCase())));
  return paramRange(p);
}

function inLimit(value, sp) {
  // null = nothing to validate; true/false = pass/fail
  if (sp == null || value === '' || value === undefined || value === null || isNaN(+value)) return null;
  const v = +value;
  if (sp.min !== undefined && sp.max !== undefined) return v >= sp.min && v <= sp.max;
  if (sp.min !== undefined) return v >= sp.min;
  if (sp.max !== undefined) return v <= sp.max;
  return null;
}

function timeUnit(tank) {   // the tank's Time param unit — decides timer -> min vs sec
  if (!tank || !Array.isArray(tank.parameters)) return 'min';
  const p = tank.parameters.find(pp => (pp.param || '').toLowerCase().startsWith('time'));
  return (p?.uom || '').toLowerCase().includes('sec') ? 'sec' : 'min';
}
function fmtClock(sec) {
  sec = Math.max(0, Math.floor(sec));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

const emptyRec = () => ({
  id: `flw-${Date.now()}`,
  route_card_no: '', wo_number: '', part_no: '', part_rev: '', qty: '',
  recipe_code: '', recipe_name: '', commodity: '',
  operator_name: '', operator_stamp: '', operator_level: '',
  rpg_no: '', batch_no: '', batch_qty: '', timer_id: '',
  status: 'OPEN', started_ts: '', completed_ts: '',
  readings: [],
  out_of_limit_count: 0, gate_fail_count: 0,
  qa_signoff_by: '', qa_signoff_ts: '', qa_disposition: '', qa_note: '',
  created_ts: new Date().toISOString(), updated_ts: '',
});

export default function RouteCardFollower() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [items, setItems] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [wos, setWos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [draft, setDraft] = useState(emptyRec());
  const [capOpen, setCapOpen] = useState(false);
  const [cap, setCap] = useState(null);      // record being captured
  const [saving, setSaving] = useState(false);
  const [timers, setTimers] = useState({});  // { [seq]: { startMs, elapsed(sec), running } } — HMI process timer
  const [, setTick] = useState(0);           // 1s tick to re-render running timers
  const [tankDevices, setTankDevices] = useState({}); // { "T-122": { device_id, temp_key, voltage_key, current_key } }
  const [autoRunning, setAutoRunning] = useState(false);

  const tankByNo = useMemo(() => {
    const m = {};
    tanks.forEach(t => { m[t.tank_no] = t; });
    return m;
  }, [tanks]);

  const woById = useMemo(() => { const m = {}; wos.forEach(w => { m[w.id] = w; }); return m; }, [wos]);
  function pickWoNew(wo_id) {
    const w = woById[wo_id];
    setDraft(d => ({ ...d, wo_id, wo_number: w?.wo_number || d.wo_number, part_no: w?.part_number || d.part_no, qty: w?.qty_ordered ?? d.qty }));
  }

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [fr, tm, sp, td] = await Promise.all([
        customerbasedshift(customerId, 'follower_records'),
        customerbasedshift(customerId, 'tank_master'),
        customerbasedshift(customerId, 'sp_recipes'),
        customerbasedshift(customerId, 'tank_devices').catch(() => []),
      ]);
      setItems(Array.isArray(fr?.[0]?.value) ? fr[0].value : []);
      setTanks(Array.isArray(tm?.[0]?.value) ? tm[0].value : []);
      setRecipes(Array.isArray(sp?.[0]?.value) ? sp[0].value : []);
      setTankDevices(td?.[0]?.value && typeof td[0].value === 'object' && !Array.isArray(td[0].value) ? td[0].value : {});
      try {
        const res = await listWorkOrders(customerId);
        setWos(Array.isArray(res?.rows) ? res.rows : (Array.isArray(res) ? res : []));
      } catch { setWos([]); }
    } catch (e) { /* leave empties */ } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  // tick every second while any process timer is running
  useEffect(() => {
    if (!Object.values(timers).some(t => t.running)) return;
    const id = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(id);
  }, [timers]);

  const totals = useMemo(() => {
    const out = { ALL: items.length, OPEN: 0, IN_PROGRESS: 0, DONE: 0, HOLD: 0 };
    items.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (filter !== 'ALL' && i.status !== filter) return false;
      if (!q) return true;
      return [i.route_card_no, i.wo_number, i.part_no, i.recipe_name, i.recipe_code, i.operator_name]
        .some(v => (v || '').toLowerCase().includes(q));
    }).sort((a, b) => new Date(b.updated_ts || b.created_ts) - new Date(a.updated_ts || a.created_ts));
  }, [items, filter, search]);

  // flatten a recipe's operations -> ordered capturable substeps.
  // Real substeps (with tank_bank_id + setpoints) preferred; template-only recipes
  // (FPI/ANODIZE/CHEM-CONV, setpoints pending) fall back to a checklist grid.
  const recipeSubsteps = useCallback((recipe) => {
    if (!recipe || !Array.isArray(recipe.operations)) return [];
    const rows = [];
    recipe.operations.forEach(op => {
      if (Array.isArray(op.substeps) && op.substeps.length) {
        op.substeps.forEach(s => rows.push({ ...s, op_name: op.name }));
      } else if (Array.isArray(op.substeps_template) && op.substeps_template.length) {
        op.substeps_template.forEach(desc => rows.push({
          description: desc, op_name: op.name,
          capture_mandatory: op.capture_mandatory || ['TIME'], _template: true,
        }));
      }
    });
    rows.forEach((r, i) => { r.seq = i + 1; });   // renumber sequentially across ops
    return rows;
  }, []);

  async function persist(next) {
    await shiftadd({ follower_records: next }, customerId, 'SERVER_SCOPE');
    setItems(next);
  }

  // ---- create a follower from a recipe ----
  function openNew() { setDraft(emptyRec()); setNewOpen(true); }

  function startCapture(rec) {
    const recipe = recipes.find(r => r.code === rec.recipe_code);
    const subs = recipeSubsteps(recipe);
    // seed readings by seq (merge existing)
    const bySeq = {}; (rec.readings || []).forEach(r => { bySeq[r.seq] = r; });
    const readings = subs.map(s => bySeq[s.seq] || {
      seq: s.seq, tank_no: s.tank_bank_id || '', description: s.description,
      mandatory: s.capture_mandatory || [], gate: s.gate ? '' : undefined,
      time: '', temp: '', ph: '', voltage: '', current: '', note: '',
    });
    setCap({ ...rec, readings, _subs: subs, _recipe: recipe });
    setCapOpen(true);
  }

  function createAndCapture() {
    if (!draft.recipe_code || !draft.wo_number) {
      Swal.fire({ icon: 'warning', title: 'Process and WO number are required', confirmButtonColor: BR.orange });
      return;
    }
    const recipe = recipes.find(r => r.code === draft.recipe_code);
    const rec = {
      ...draft,
      recipe_name: recipe?.name || draft.recipe_code,
      commodity: recipe?.commodity || '',
      status: 'IN_PROGRESS',
      started_ts: new Date().toISOString(),
    };
    setNewOpen(false);
    startCapture(rec);
  }

  // ---- compute status + counts from readings ----
  function evaluate(rec, subs) {
    let oo = 0, gf = 0, filledAll = true;
    (rec.readings || []).forEach((r, idx) => {
      const s = subs[idx] || {};
      const tank = tankByNo[(r.tank_no || '').split('/')[0]];
      (r.mandatory || []).forEach(mk => {
        const col = COLS.find(c => c.key === mk); if (!col) return;
        const val = r[col.field];
        if (val === '' || val === undefined) { filledAll = false; return; }
        const sp = setpointFor(tank, col, s.setpoint_override, s.setpoints);
        const lim = inLimit(val, sp);
        if (lim === false) oo += 1;
      });
      if (s.gate && (r.gate === 'FAIL')) gf += 1;
      if (s.gate && !r.gate) filledAll = false;
    });
    let status = 'IN_PROGRESS';
    if (oo > 0 || gf > 0) status = 'HOLD';
    else if (filledAll) status = 'DONE';
    return { out_of_limit_count: oo, gate_fail_count: gf, status };
  }

  async function saveCapture() {
    if (!cap) return;
    setSaving(true);
    try {
      const ev = evaluate(cap, cap._subs);
      const clean = { ...cap };
      delete clean._subs; delete clean._recipe;
      const rec = {
        ...clean, ...ev,
        completed_ts: ev.status === 'DONE' ? (cap.completed_ts || new Date().toISOString()) : cap.completed_ts,
        updated_ts: new Date().toISOString(),
      };
      const exists = items.findIndex(i => i.id === rec.id);
      const next = exists === -1 ? [...items, rec] : items.map((it, idx) => idx === exists ? rec : it);
      await persist(next);
      setCapOpen(false); setCap(null);
      Swal.fire({
        toast: true, position: 'top-end', timer: 2200, showConfirmButton: false,
        icon: ev.status === 'HOLD' ? 'warning' : 'success',
        title: ev.status === 'HOLD'
          ? `Saved — ${ev.out_of_limit_count} out-of-limit / ${ev.gate_fail_count} gate-fail → Quality hold`
          : `Follower saved (${ev.status})`,
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: e.message, confirmButtonColor: BR.orange });
    } finally { setSaving(false); }
  }

  async function qaSignoff(item) {
    const flagged = (item.out_of_limit_count || 0) + (item.gate_fail_count || 0);
    const { value: form } = await Swal.fire({
      title: `QA sign-off · ${item.route_card_no || item.wo_number}`,
      html: `
        <div style="text-align:left; font-size:13px;">
          <div style="background:${flagged ? '#fef3c7' : '#f0fdf4'}; padding:10px; border-radius:6px; margin-bottom:12px;">
            <strong>Process:</strong> ${item.recipe_name} (${item.recipe_code})<br/>
            <strong>WO:</strong> ${item.wo_number || '—'} · <strong>Part:</strong> ${item.part_no || '—'} ${item.part_rev || ''}<br/>
            <strong>Operator:</strong> ${item.operator_name || '?'} ${item.operator_stamp ? '· ' + item.operator_stamp : ''}<br/>
            <strong>Out-of-limit:</strong> ${item.out_of_limit_count || 0} · <strong>Gate fails:</strong> ${item.gate_fail_count || 0}
          </div>
          <label style="font-weight:700; display:block; margin-bottom:6px;">Disposition</label>
          <select id="qa-disp" style="width:100%; padding:10px; font-size:14px; font-weight:600; border:1.5px solid #d1d5db; border-radius:6px;">
            <option value="ACCEPT">✓ Accept — within limits</option>
            <option value="CONCESSION" ${flagged ? 'selected' : ''}>⚠ Accept on concession (documented)</option>
            <option value="REJECT">✗ Reject — re-process / scrap</option>
          </select>
          <label style="font-weight:700; display:block; margin-top:12px; margin-bottom:6px;">QA name / stamp</label>
          <input id="qa-name" placeholder="Your name (sign-off)" style="width:100%; padding:9px 12px; font-size:14px; border:1.5px solid #d1d5db; border-radius:6px;"/>
          <label style="font-weight:700; display:block; margin-top:12px; margin-bottom:6px;">Note</label>
          <textarea id="qa-note" rows="2" style="width:100%; padding:8px; font-size:13px; border:1.5px solid #d1d5db; border-radius:6px; font-family:inherit;"></textarea>
        </div>`,
      icon: 'info', showCancelButton: true,
      confirmButtonText: 'Sign off', confirmButtonColor: BR.orange,
      cancelButtonText: 'Cancel', reverseButtons: true,
      preConfirm: () => {
        const disp = document.getElementById('qa-disp').value;
        const name = document.getElementById('qa-name').value.trim();
        const note = document.getElementById('qa-note').value.trim();
        if (!name) { Swal.showValidationMessage('QA name required for sign-off'); return false; }
        return { disp, name, note };
      },
    });
    if (!form) return;
    const next = items.map(i => i.id === item.id ? {
      ...i,
      qa_disposition: form.disp, qa_signoff_by: form.name, qa_signoff_ts: new Date().toISOString(),
      qa_note: form.note, status: form.disp === 'REJECT' ? 'HOLD' : 'DONE',
      updated_ts: new Date().toISOString(),
    } : i);
    await persist(next);
    Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
      icon: 'success', title: `Signed off by ${form.name}` });
  }

  // ---- print a paper-faithful Route Card Follower (audit binder) ----
  function printFollower(item) {
    const recipe = recipes.find(r => r.code === item.recipe_code);
    const subs = recipeSubsteps(recipe);
    const bySeq = {}; (item.readings || []).forEach(r => { bySeq[r.seq] = r; });
    const esc = s => String(s == null ? '' : s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const op1 = (recipe?.operations || [])[0] || {};
    const specLine = `${recipe?.internal_spec || ''}${recipe?.spec_rev ? ' Rev ' + recipe.spec_rev : ''}`;
    let oo = 0, gf = 0;
    const rows = subs.map(s => {
      const r = bySeq[s.seq] || {};
      const tank = tankByNo[(s.tank_bank_id || '').split('/')[0]];
      const cells = COLS.map(col => {
        if (!(s.capture_mandatory || []).includes(col.key)) return '<td></td>';
        const sp = setpointFor(tank, col, s.setpoint_override, s.setpoints);
        const val = r[col.field];
        const lim = inLimit(val, sp);
        if (lim === false) oo++;
        const cls = 'mand' + (lim === false ? ' bad' : '');
        const shown = (val !== '' && val != null) ? esc(val) : '—';
        return `<td class="${cls}"><span class="v">${shown}</span>${sp && sp.display ? `<span class="sp">${esc(sp.display)}</span>` : ''}</td>`;
      }).join('');
      let gate = '<td></td>';
      if (s.gate) { if (r.gate === 'FAIL') gf++; gate = `<td class="${r.gate === 'FAIL' ? 'bad' : ''}"><b>${r.gate || '—'}</b></td>`; }
      return `<tr><td class="n">${s.seq}</td><td class="tk">${esc(s.tank_bank_id || '')}</td><td class="d">${esc(s.description)}${s.gate ? `<span class="sp">${esc(s.gate.criteria || '')}</span>` : ''}</td>${cells}${gate}</tr>`;
    }).join('');
    const flagged = oo + gf;
    const stamp = { OPEN: '#6b7280', IN_PROGRESS: '#f97316', DONE: '#16a34a', HOLD: '#f59e0b' }[item.status] || '#6b7280';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Follower ${esc(item.route_card_no || item.wo_number)} ${esc(item.recipe_code)}</title>
<style>
@page { size: A4 portrait; margin: 11mm; }
body { font-family: Arial, Helvetica, sans-serif; color:#111; margin:0; font-size:11px; }
.top { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #111; padding-bottom:6px; }
.brand { font-size:15px; font-weight:800; } .brand span{ color:#f97316; }
.doc { text-align:center; flex:1; } .doc h2{ margin:0; font-size:15px; letter-spacing:.5px; }
.meta { font-size:10px; text-align:right; }
.sub { margin:8px 0; font-size:11px; } .sub b{ display:inline-block; min-width:0; }
.badge { display:inline-block; padding:2px 10px; border-radius:4px; color:#fff; font-weight:800; font-size:11px; background:${stamp}; }
table { width:100%; border-collapse:collapse; margin-top:8px; }
th,td { border:1px solid #888; padding:3px 4px; vertical-align:top; }
th { background:#efefef; font-size:9px; text-transform:uppercase; letter-spacing:.3px; }
td.mand { background:#e5e7eb; text-align:center; }
td.bad { background:#fde2e2; }
td.bad .v { color:#b91c1c; }
.v { font-weight:700; font-size:11px; } .sp{ display:block; font-size:8px; color:#555; font-weight:400; margin-top:1px; }
td.n{ text-align:center; color:#666; width:22px; } td.tk{ font-family:monospace; font-weight:700; white-space:nowrap; } td.d{ font-size:10px; }
.legend { margin-top:6px; font-size:9px; color:#555; } .swatch{ display:inline-block; width:14px; height:10px; background:#e5e7eb; border:1px solid #888; vertical-align:middle; margin-right:3px; }
.sign { margin-top:22px; display:flex; justify-content:space-between; font-size:10px; }
.sign .box { width:31%; border-top:1px solid #111; padding-top:4px; }
.approve { margin-top:16px; font-size:10px; font-weight:700; }
.no-print { text-align:center; margin:10px; }
.no-print button { padding:8px 20px; font-size:13px; font-weight:700; background:#f97316; color:#fff; border:none; border-radius:6px; cursor:pointer; }
@media print { .no-print { display:none; } }
</style></head><body>
<div class="no-print"><button onclick="window.print()">🖨 Print / Save as PDF</button></div>
<div class="top">
  <div class="brand">PMI <span>Global Technologies</span></div>
  <div class="doc"><h2>ROUTE CARD FOLLOWER</h2><div style="font-size:10px">${esc(recipe?.name || item.recipe_name)}</div></div>
  <div class="meta">Route Card: <b>${esc(item.route_card_no || '—')}</b><br/>Form PGT-ME-F-29 Rev A</div>
</div>
<div class="sub">
  For the Operation: <b>${esc(op1.name || item.recipe_name)}</b> &nbsp; Internal Spec: <b>${esc(specLine || '—')}</b>${recipe?.customer_spec ? ' &nbsp; (' + esc(recipe.customer_spec) + ')' : ''}<br/>
  WO <b>${esc(item.wo_number || '—')}</b> &nbsp; Part <b>${esc(item.part_no || '—')} ${esc(item.part_rev || '')}</b> &nbsp; Qty <b>${esc(item.qty || '—')}</b>
  ${item.rpg_no ? ' &nbsp; RPG <b>' + esc(item.rpg_no) + '</b>' : ''}${item.batch_no ? ' &nbsp; Batch <b>' + esc(item.batch_no) + '</b>' : ''}
  &nbsp; <span class="badge">${esc(item.status)}</span> ${flagged ? `<span style="color:#b91c1c;font-weight:800"> · ${flagged} out-of-limit/gate</span>` : ''}
</div>
<table>
  <thead><tr>
    <th>#</th><th>Tank</th><th>Description</th><th>Time</th><th>Temp °C</th><th>pH</th><th>Voltage</th><th>Current</th><th>Gate</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="legend"><span class="swatch"></span> Grey columns are mandatory, filled by the operator against PCM-01 tank limits (shown small). Red = out of limit.</div>
<div class="sign">
  <div class="box">Operator: <b>${esc(item.operator_name || '')}</b> ${item.operator_stamp ? '· ' + esc(item.operator_stamp) : ''}<br/>Sign / Stamp / Date</div>
  <div class="box">QA: <b>${esc(item.qa_signoff_by || '')}</b> ${item.qa_disposition ? '· ' + esc(item.qa_disposition) : ''}<br/>Sign / Stamp / Date ${item.qa_signoff_ts ? '· ' + esc(fmtDate(item.qa_signoff_ts)) : ''}</div>
  <div class="box">&nbsp;<br/>Reviewed / Date</div>
</div>
<div class="approve">Approved for PMI Global Technologies Private Limited</div>
</body></html>`;
    const w = window.open('', '_blank', 'width=980,height=1200');
    if (w) { w.document.write(html); w.document.close(); }
    else Swal.fire({ icon: 'warning', title: 'Pop-up blocked', text: 'Allow pop-ups to print the follower.', confirmButtonColor: BR.orange });
  }

  // ---- HMI process timer: operator starts on tank-in, stops on tank-out; time auto-records ----
  function startTimer(seq) {
    setTimers(prev => ({ ...prev, [seq]: { startMs: Date.now(), elapsed: prev[seq]?.elapsed || 0, running: true } }));
  }
  function stopTimer(seq, idx, unit) {
    const t = timers[seq]; if (!t?.running) return;
    const total = (t.elapsed || 0) + (Date.now() - t.startMs) / 1000;
    const val = unit === 'sec' ? Math.round(total) : Math.round((total / 60) * 10) / 10;  // min to 0.1
    setReading(idx, 'time', String(val));
    setTimers(prev => ({ ...prev, [seq]: { ...prev[seq], elapsed: total, running: false } }));
  }
  const timerSec = (seq) => { const t = timers[seq]; if (!t) return 0; return (t.elapsed || 0) + (t.running ? (Date.now() - t.startMs) / 1000 : 0); };

  // ---- PLC / rectifier auto-fill: pull latest telemetry for a mapped tank device (temp/voltage/current) ----
  async function pullPlc(idx, tankNo) {
    const dev = tankDevices[tankNo];
    if (!dev || !dev.device_id) { Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false, icon: 'info', title: `No PLC device mapped for ${tankNo}` }); return; }
    try {
      const base = window._env_.SERVER_URL.replace(/\/$/, '');
      const keys = [dev.temp_key, dev.voltage_key, dev.current_key].filter(Boolean).join(',');
      const r = await fetch(`${base}/api/plugins/telemetry/DEVICE/${dev.device_id}/values/timeseries?keys=${keys}`, { headers: { 'X-Authorization': `Bearer ${localStorage.getItem('token1')}` } });
      const data = await r.json();
      const latest = k => (k && data?.[k]?.[0]?.value != null ? String(data[k][0].value) : undefined);
      const upd = {}; const tv = latest(dev.temp_key), vv = latest(dev.voltage_key), cv = latest(dev.current_key);
      if (tv !== undefined) upd.temp = tv; if (vv !== undefined) upd.voltage = vv; if (cv !== undefined) upd.current = cv;
      if (!Object.keys(upd).length) throw new Error('no telemetry');
      setCap(prev => ({ ...prev, readings: prev.readings.map((rr, i) => i === idx ? { ...rr, ...upd } : rr) }));
      Swal.fire({ toast: true, position: 'top-end', timer: 1600, showConfirmButton: false, icon: 'success', title: `${tankNo}: pulled from PLC` });
    } catch (e) {
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false, icon: 'warning', title: `${tankNo}: no live PLC reading` });
    }
  }

  // ---- simulated tank signal: in-band value per parameter (setpoint optimum; real PLC when a device is mapped) ----
  function simValue(tank, col, override, inline) {
    const p = tank && Array.isArray(tank.parameters)
      ? tank.parameters.find(pp => col.names.some(n => (pp.param || '').toLowerCase().startsWith(n.toLowerCase()))) : null;
    if (p && p.optimum !== undefined && !isNaN(+p.optimum)) return +p.optimum;
    const sp = setpointFor(tank, col, override, inline);
    if (sp) {
      if (sp.min !== undefined && sp.max !== undefined) return Math.round(((sp.min + sp.max) / 2) * 10) / 10;
      if (sp.min !== undefined) return sp.min; if (sp.max !== undefined) return sp.max;
    }
    // no numeric target (ambient / RPG-programmed): pick a representative in-band value
    return col.field === 'time' ? 25 : col.field === 'temp' ? 25 : col.field === 'ph' ? 7 : '';
  }
  function fillReading(s, tank) {
    const out = {};
    (s.capture_mandatory || []).forEach(mk => { const col = COLS.find(c => c.key === mk); if (!col) return; const v = simValue(tank, col, s.setpoint_override, s.setpoints); if (v !== '' && v != null) out[col.field] = String(v); });
    if (s.gate) out.gate = 'PASS';
    return out;
  }

  // ---- Layer 2: hands-off auto-run — real clock-in -> tank signal fills grid -> auto-complete -> real clock-out ----
  async function autoRun(item) {
    if (autoRunning) return;
    let opName = item.operator_name, opId = item.operator_stamp || item.operator_name;
    if (!opName) {
      const { value } = await Swal.fire({ title: 'Operator on this process', input: 'text', inputPlaceholder: 'Operator name', confirmButtonColor: BR.orange, showCancelButton: true });
      if (!value) return; opName = value; opId = value;
    }
    setAutoRunning(true);
    const delay = ms => new Promise(r => setTimeout(r, ms));
    // 1. real clock-in — operator now shows on the Labour Board for this WO/op
    try { await clockIn({ customer_id: customerId, operator_id: opId, operator_name: opName, activity: 'SPECIAL_PROCESS', wo_number: item.wo_number, op_seq: item.op_no, source: 'follower', notes: `${item.recipe_code} follower` }); } catch (e) { /* non-fatal for demo */ }
    const recipe = recipes.find(r => r.code === item.recipe_code);
    const subs = recipeSubsteps(recipe);
    let readings = subs.map(s => ({ seq: s.seq, tank_no: s.tank_bank_id || '', description: s.description, mandatory: s.capture_mandatory || [], gate: s.gate ? '' : undefined, time: '', temp: '', ph: '', voltage: '', current: '', note: '' }));
    const started = new Date().toISOString();
    setCap({ ...item, operator_name: opName, operator_stamp: opId, status: 'IN_PROGRESS', started_ts: started, readings, _subs: subs, _recipe: recipe });
    setCapOpen(true);
    // 2. play the signal — fill each tank step in sequence (sped up for the floor demo)
    for (let i = 0; i < subs.length; i++) {
      await delay(450);
      const s = subs[i]; const tank = tankByNo[(s.tank_bank_id || '').split('/')[0]];
      readings = readings.map((rr, idx) => idx === i ? { ...rr, ...fillReading(s, tank) } : rr);
      setCap(prev => prev ? { ...prev, readings } : prev);
    }
    await delay(400);
    // 3. auto-validate + auto-complete
    const ev = evaluate({ readings }, subs);
    const rec = { ...item, operator_name: opName, operator_stamp: opId, recipe_name: recipe?.name || item.recipe_name, commodity: recipe?.commodity || item.commodity, readings, ...ev, started_ts: started, completed_ts: new Date().toISOString(), updated_ts: new Date().toISOString() };
    const exists = items.findIndex(x => x.id === rec.id);
    await persist(exists === -1 ? [...items, rec] : items.map((x, k) => k === exists ? rec : x));
    setCap(prev => prev ? { ...prev, ...ev } : prev);
    // 4. real clock-out
    try { await clockOut({ customer_id: customerId, operator_id: opId, operator_name: opName, notes: `${item.recipe_code} auto-complete (${ev.status})` }); } catch (e) { /* non-fatal */ }
    setAutoRunning(false);
    Swal.fire({ toast: true, position: 'top-end', timer: 2800, showConfirmButton: false, icon: ev.status === 'HOLD' ? 'warning' : 'success', title: ev.status === 'HOLD' ? `Auto-run: ${ev.out_of_limit_count} out-of-limit → Quality hold · clocked out` : `Auto-completed (${ev.status}) · operator clocked out` });
  }

  const setReading = (idx, field, value) => {
    setCap(prev => {
      const readings = prev.readings.map((r, i) => i === idx ? { ...r, [field]: value } : r);
      return { ...prev, readings };
    });
  };

  // extra fields present anywhere in this recipe (rpg/batch/timer)
  const capExtras = useMemo(() => {
    if (!cap?._subs) return [];
    const set = new Set();
    cap._subs.forEach(s => (s.extra_fields || []).forEach(f => set.add(f)));
    return [...set].filter(f => ['rpg_no', 'batch_no', 'batch_qty', 'timer_id'].includes(f));
  }, [cap]);

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>🧪 Route Card Follower</h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Per-operation parameter capture · setpoints from PCM-01 tank master · water-break gates · out-of-limit → Quality · operator &amp; QA sign-off
          </div>
        </div>
        <button onClick={openNew}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px',
                   background: BR.orange, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> New Follower
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { key: 'ALL', label: 'All followers', color: BR.dark },
          { key: 'OPEN', label: 'Open', color: BR.muted },
          { key: 'IN_PROGRESS', label: 'In progress', color: BR.orange },
          { key: 'DONE', label: 'Done', color: BR.green },
          { key: 'HOLD', label: 'Quality hold', color: BR.amber },
        ].map(t => (
          <div key={t.key} onClick={() => setFilter(t.key)}
            style={{ background: '#fff', border: `2px solid ${filter === t.key ? t.color : BR.border}`, borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>{totals[t.key] ?? 0}</div>
          </div>
        ))}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search route card, WO, part, process, operator…"
        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BR.border}`, borderRadius: 6, fontSize: 13, marginBottom: 10 }} />

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No followers {filter !== 'ALL' ? `in ${filter}` : 'yet'}. Click "New Follower" to start a special-process operation.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Status', 'Route Card', 'WO', 'Part', 'Process', 'Operator', 'Progress', 'Flags', 'QA', 'Updated', ''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const info = STATUS_INFO[i.status] || STATUS_INFO.OPEN;
                const total = (i.readings || []).length;
                const filled = (i.readings || []).filter(r => (r.mandatory || []).every(mk => {
                  const col = COLS.find(c => c.key === mk); return col && r[col.field] !== '' && r[col.field] !== undefined;
                }) && (r.gate === undefined || r.gate)).length;
                const flags = (i.out_of_limit_count || 0) + (i.gate_fail_count || 0);
                return (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10, background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>{info.label}</span>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 700 }}>{i.route_card_no || '—'}</td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{i.wo_number || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>{i.part_no || '—'}</div>
                      <div style={{ fontSize: 10, color: BR.muted }}>{i.part_rev ? `rev ${i.part_rev}` : ''}</div>
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ fontWeight: 700, color: BR.blue }}>{i.recipe_code}</span>
                      <div style={{ fontSize: 10, color: BR.muted }}>{i.commodity}</div>
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <div>{i.operator_name || '—'}</div>
                      <div style={{ fontSize: 10, color: BR.muted }}>{i.operator_stamp || ''}</div>
                    </td>
                    <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 800, color: filled === total && total ? BR.green : BR.orange }}>{filled}</span>
                      <span style={{ color: BR.muted }}>/{total}</span>
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      {flags > 0
                        ? <span style={{ fontWeight: 800, color: BR.red }}>⚠ {flags}</span>
                        : <span style={{ color: BR.green }}>✓</span>}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      {i.qa_signoff_by
                        ? <span style={{ fontSize: 11, fontWeight: 700, color: i.qa_disposition === 'REJECT' ? BR.red : BR.green }}><VerifiedIcon fontSize="inherit" /> {i.qa_signoff_by}</span>
                        : (i.status === 'DONE' || i.status === 'HOLD')
                          ? <button onClick={() => qaSignoff(i)} style={{ padding: '3px 8px', background: BR.orange, color: '#fff', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>Sign off</button>
                          : <span style={{ color: BR.muted, fontSize: 11 }}>—</span>}
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: 11, color: BR.muted }}>{fmtDate(i.updated_ts || i.created_ts)}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {(i.status === 'OPEN' || i.status === 'IN_PROGRESS') && (
                        <Tooltip title="Auto-run: clock-in → tank signal fills → auto-complete → clock-out">
                          <IconButton size="small" onClick={() => autoRun(i)} disabled={autoRunning}>
                            <BoltIcon fontSize="small" sx={{ color: BR.orange }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Open capture (manual / timer)">
                        <IconButton size="small" onClick={() => startCapture(i)}><ScienceIcon fontSize="small" sx={{ color: BR.blue }} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Print follower (PDF)">
                        <IconButton size="small" onClick={() => printFollower(i)}><PrintIcon fontSize="small" sx={{ color: BR.dark }} /></IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6 }}>
        <strong style={{ color: '#1e3a8a' }}>NADCAP note:</strong> The Follower is the audit-of-record for a special process. Every mandatory (grey) column must be filled by the operator against the tank's approved limits (PCM-01). Any out-of-limit reading or failed water-break check routes the lot to Quality (blue-card) before release. Setpoints are configuration, not code — a new process appears here automatically once its recipe + tanks are defined.
      </div>

      {/* ---- New follower dialog ---- */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>New Route Card Follower</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <TextField label="Special Process *" size="small" fullWidth select value={draft.recipe_code}
              onChange={e => setDraft({ ...draft, recipe_code: e.target.value })} sx={{ gridColumn: '1 / span 2' }}>
              <MenuItem value="">— Select process —</MenuItem>
              {recipes.map(r => <MenuItem key={r.code} value={r.code}>{r.code} · {r.name}</MenuItem>)}
            </TextField>
            <TextField label="Work Order" size="small" fullWidth select value={draft.wo_id || ''}
              onChange={e => pickWoNew(e.target.value)} sx={{ gridColumn: '1 / span 2' }}
              helperText={wos.length ? 'Pick a live WO to auto-fill part & qty' : 'No live work orders loaded — enter manually below'}>
              <MenuItem value="">— Select Work Order —</MenuItem>
              {wos.map(w => <MenuItem key={w.id} value={w.id}>{w.wo_number} · {w.part_number} · {w.status}</MenuItem>)}
            </TextField>
            <TextField label="Route Card No" size="small" fullWidth value={draft.route_card_no}
              onChange={e => setDraft({ ...draft, route_card_no: e.target.value })} placeholder="1600090239" />
            <TextField label="WO Number *" size="small" fullWidth value={draft.wo_number}
              onChange={e => setDraft({ ...draft, wo_number: e.target.value })} placeholder="PMI-SP-01" />
            <TextField label="Part No" size="small" fullWidth value={draft.part_no}
              onChange={e => setDraft({ ...draft, part_no: e.target.value })} placeholder="77FV202-2-00" />
            <TextField label="Part Rev" size="small" fullWidth value={draft.part_rev}
              onChange={e => setDraft({ ...draft, part_rev: e.target.value })} placeholder="B" />
            <TextField label="Qty" size="small" fullWidth type="number" value={draft.qty}
              onChange={e => setDraft({ ...draft, qty: e.target.value })} />
            <TextField label="Operator" size="small" fullWidth value={draft.operator_name}
              onChange={e => setDraft({ ...draft, operator_name: e.target.value })} />
            <TextField label="Operator Stamp" size="small" fullWidth value={draft.operator_stamp}
              onChange={e => setDraft({ ...draft, operator_stamp: e.target.value })} placeholder="PGT SP 07" />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button onClick={createAndCapture} variant="contained" sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            Start capture
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Capture dialog (the follower grid) ---- */}
      <Dialog open={capOpen} onClose={() => setCapOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
          {cap?.recipe_name} <span style={{ color: BR.muted, fontWeight: 600, fontSize: 13 }}>({cap?.recipe_code})</span>
          <div style={{ fontSize: 12, color: BR.muted, fontWeight: 500, marginTop: 2 }}>
            Route {cap?.route_card_no || '—'} · WO {cap?.wo_number} · {cap?.part_no} {cap?.part_rev} · Operator {cap?.operator_name || '—'}
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {capExtras.length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              {capExtras.map(f => (
                <TextField key={f} size="small" label={{ rpg_no: 'RPG No', batch_no: 'Batch No', batch_qty: 'Batch Qty', timer_id: 'Timer ID' }[f]}
                  value={cap[f] || ''} onChange={e => setCap({ ...cap, [f]: e.target.value })} sx={{ width: 150 }} />
              ))}
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `2px solid ${BR.border}` }}>
                <th style={{ padding: '7px 8px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: BR.muted, width: 32 }}>#</th>
                <th style={{ padding: '7px 8px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: BR.muted, width: 74 }}>Tank</th>
                <th style={{ padding: '7px 8px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: BR.muted }}>Description</th>
                {COLS.map(c => (
                  <th key={c.key} style={{ padding: '7px 8px', textAlign: 'center', fontSize: 10, fontWeight: 800, color: BR.muted, width: 92 }}>{c.label}{c.unit ? ` (${c.unit})` : ''}</th>
                ))}
                <th style={{ padding: '7px 8px', textAlign: 'center', fontSize: 10, fontWeight: 800, color: BR.muted, width: 120 }}>Gate</th>
              </tr>
            </thead>
            <tbody>
              {(cap?.readings || []).map((r, idx) => {
                const s = cap._subs[idx] || {};
                const tankNo = (r.tank_no || '').split('/')[0];
                const tank = tankByNo[tankNo];
                const noteOnly = s.note === true;
                return (
                  <tr key={idx} style={{ borderBottom: `1px solid ${BR.border}`, background: noteOnly ? '#fcfcfd' : '#fff' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 700, color: BR.muted }}>{r.seq}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 700, color: tank ? BR.dark : BR.muted }}>
                      {r.tank_no || '—'}
                      {s.conditional && <div style={{ fontSize: 9, color: BR.amber, fontWeight: 700 }}>if needed</div>}
                    </td>
                    <td style={{ padding: '6px 8px', color: noteOnly ? BR.muted : BR.dark, fontStyle: noteOnly ? 'italic' : 'normal' }}>
                      {r.description}
                      {tank && <div style={{ fontSize: 9, color: BR.muted }}>{tank.process}{tank.content ? ` · ${tank.content}` : ''}</div>}
                    </td>
                    {COLS.map(col => {
                      const mand = (r.mandatory || []).includes(col.key);
                      if (!mand) return <td key={col.key} style={{ background: '#fafafa' }} />;
                      const sp = setpointFor(tank, col, s.setpoint_override, s.setpoints);
                      const lim = inLimit(r[col.field], sp);
                      const bc = lim === false ? BR.red : lim === true ? BR.green : '#d1d5db';
                      const isTime = col.field === 'time';
                      const isPlcCol = col.field === 'temp' || col.field === 'voltage' || col.field === 'current';
                      const hasDev = !!(tankDevices[tankNo] && tankDevices[tankNo].device_id);
                      const tRun = timers[r.seq]?.running;
                      return (
                        <td key={col.key} style={{ padding: '4px 5px', textAlign: 'center' }}>
                          <input type="number" value={r[col.field] ?? ''} inputMode="decimal"
                            onChange={e => setReading(idx, col.field, e.target.value)}
                            style={{ width: 72, padding: '5px 6px', textAlign: 'center', border: `1.5px solid ${bc}`, borderRadius: 5, fontSize: 12, fontWeight: 700,
                                     background: lim === false ? '#fef2f2' : lim === true ? '#f0fdf4' : '#fff' }} />
                          {isTime && (
                            <div style={{ marginTop: 2 }}>
                              {tRun
                                ? <button onClick={() => stopTimer(r.seq, idx, timeUnit(tank))} style={{ width: 72, padding: '2px 0', fontSize: 9.5, fontWeight: 800, border: 'none', borderRadius: 4, cursor: 'pointer', background: BR.red, color: '#fff' }}>■ {fmtClock(timerSec(r.seq))}</button>
                                : <button onClick={() => startTimer(r.seq)} style={{ width: 72, padding: '2px 0', fontSize: 9.5, fontWeight: 800, border: `1px solid ${BR.orange}`, borderRadius: 4, cursor: 'pointer', background: '#fff', color: BR.orange }}>▶ timer</button>}
                            </div>
                          )}
                          {isPlcCol && hasDev && (
                            <div style={{ marginTop: 2 }}>
                              <button onClick={() => pullPlc(idx, tankNo)} title="Pull latest from PLC" style={{ width: 72, padding: '2px 0', fontSize: 9.5, fontWeight: 800, border: `1px solid ${BR.blue}`, borderRadius: 4, cursor: 'pointer', background: '#fff', color: BR.blue }}>⚡ PLC</button>
                            </div>
                          )}
                          {sp?.display && <div style={{ fontSize: 8.5, color: lim === false ? BR.red : BR.muted, marginTop: 1, lineHeight: 1.1 }}>{sp.display}</div>}
                        </td>
                      );
                    })}
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {s.gate ? (
                        <div>
                          <div style={{ display: 'inline-flex', border: `1px solid ${BR.border}`, borderRadius: 5, overflow: 'hidden' }}>
                            {['PASS', 'FAIL'].map(g => (
                              <button key={g} onClick={() => setReading(idx, 'gate', g)}
                                style={{ padding: '4px 8px', fontSize: 10, fontWeight: 800, cursor: 'pointer', border: 'none',
                                         background: r.gate === g ? (g === 'PASS' ? BR.green : BR.red) : '#fff',
                                         color: r.gate === g ? '#fff' : BR.muted }}>{g}</button>
                            ))}
                          </div>
                          <div style={{ fontSize: 8.5, color: BR.muted, marginTop: 2 }}>{s.gate.criteria}</div>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <div style={{ fontSize: 11, color: BR.muted }}>
            <GavelIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} /> Out-of-limit or failed gate → auto Quality hold on save.
          </div>
          <div>
            <Button onClick={() => setCapOpen(false)}>Close</Button>
            <Button onClick={() => cap && printFollower(cap)} startIcon={<PrintIcon />} sx={{ color: BR.dark }}>Print</Button>
            <Button onClick={saveCapture} disabled={saving} variant="contained" sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
              {saving ? 'Saving…' : 'Save follower'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
}
